import React, { useCallback, useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/** Full-screen photo viewer: arrows, swipe, Esc and backdrop close. */
export const PhotoLightbox = ({ images, index, alt, onClose, onIndexChange }) => {
  const [touchStart, setTouchStart] = useState(null);
  const count = images.length;

  const step = useCallback((delta) => {
    onIndexChange((index + delta + count) % count);
  }, [index, count, onIndexChange]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') step(1);
      if (e.key === 'ArrowLeft') step(-1);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose, step]);

  return (
    <div className="fixed inset-0 z-[120] flex flex-col bg-ei-navy/95 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose} data-testid="photo-lightbox">
      <div className="flex items-center justify-between px-4 py-4 sm:px-8">
        <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-ei-white/70" data-testid="photo-lightbox-counter">
          {index + 1} / {count}
        </p>
        <button type="button" onClick={onClose} aria-label="Close photo viewer"
          className="rounded-full border border-white/25 p-2.5 text-ei-white transition-colors hover:bg-white/10"
          data-testid="photo-lightbox-close">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex flex-1 items-center justify-center px-4 pb-6 sm:px-16"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => setTouchStart(e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (touchStart === null) return;
          const delta = e.changedTouches[0].clientX - touchStart;
          if (Math.abs(delta) > 50) step(delta < 0 ? 1 : -1);
          setTouchStart(null);
        }}>
        {count > 1 && (
          <button type="button" onClick={() => step(-1)} aria-label="Previous photo"
            className="mr-2 hidden rounded-full border border-white/25 p-3 text-ei-white transition-colors hover:bg-white/10 sm:block"
            data-testid="photo-lightbox-prev">
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        <img src={images[index]} alt={alt} className="max-h-[80vh] max-w-full select-none object-contain" data-testid="photo-lightbox-image" />
        {count > 1 && (
          <button type="button" onClick={() => step(1)} aria-label="Next photo"
            className="ml-2 hidden rounded-full border border-white/25 p-3 text-ei-white transition-colors hover:bg-white/10 sm:block"
            data-testid="photo-lightbox-next">
            <ChevronRight className="h-6 w-6" />
          </button>
        )}
      </div>

      {count > 1 && (
        <p className="pb-6 text-center text-xs text-ei-white/55 sm:hidden">Swipe to see the other photos</p>
      )}
    </div>
  );
};
